import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2, ShoppingCart, ArrowRight, Briefcase } from "lucide-react";
import { useQuotation } from "../context/QuotationContext";

const QuotationBasketDrawer = () => {
  const navigate = useNavigate();
  const {
    selectedItems,
    itemCount,
    removeItem,
    isDrawerOpen,
    closeDrawer,
  } = useQuotation();

  const goToCheckout = () => {
    closeDrawer();
    navigate("/contacto", { state: { fromBasket: true } });
  };

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.button
            type="button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1100] bg-black/40 backdrop-blur-[2px]"
            aria-label="Cerrar cotización"
            onClick={closeDrawer}
          />

          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label="Resumen de cotización"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="fixed z-[1101] left-3 right-3 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom,0px)+12px)] md:left-auto md:right-6 md:bottom-auto md:top-[84px] md:w-[400px] max-h-[min(70vh,520px)] flex flex-col bg-white rounded-[28px] shadow-2xl border border-brand-soft overflow-hidden xl:max-h-[min(75vh,560px)]"
          >
            <header className="flex items-center justify-between px-5 py-4 border-b border-brand-soft bg-[#f8faf9] shrink-0">
              <div className="flex items-center gap-2">
                <ShoppingCart className="text-brand-primary" size={20} />
                <h2 className="font-display font-black text-brand-dark text-sm uppercase tracking-widest">
                  Tu cotización
                </h2>
                <span
                  className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-brand-primary text-white text-[11px] font-black flex items-center justify-center"
                  aria-live="polite"
                >
                  {itemCount}
                </span>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="w-9 h-9 rounded-full bg-white border border-brand-soft flex items-center justify-center text-brand-dark/50 hover:text-brand-dark transition-colors"
                aria-label="Cerrar panel"
              >
                <X size={18} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {itemCount === 0 ? (
                <div className="text-center py-10 px-4">
                  <div className="w-14 h-14 rounded-2xl bg-brand-soft/50 flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart className="text-brand-primary/40" size={28} />
                  </div>
                  <p className="font-display font-bold text-brand-dark mb-2">Sin servicios aún</p>
                  <p className="text-sm text-brand-dark/60 mb-6 leading-relaxed">
                    Explore el catálogo y use el icono de carrito para armar su solicitud.
                  </p>
                  <Link
                    to="/servicios"
                    onClick={closeDrawer}
                    className="inline-flex items-center gap-2 text-brand-primary font-bold text-xs uppercase tracking-widest hover:text-brand-dark transition-colors"
                  >
                    Explorar servicios <ArrowRight size={14} />
                  </Link>
                </div>
              ) : (
                selectedItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 items-start p-4 bg-[#f8faf9] rounded-2xl border border-brand-soft"
                  >
                    <div className="w-9 h-9 rounded-xl bg-brand-primary/10 flex items-center justify-center shrink-0">
                      <Briefcase className="text-brand-primary" size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-brand-dark text-sm leading-snug">{item.name}</p>
                      {item.category && (
                        <p className="text-[10px] font-bold uppercase tracking-widest text-brand-primary/60 mt-1">
                          {item.category}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="p-2 rounded-xl text-brand-dark/25 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                      aria-label={`Quitar ${item.name}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>

            <footer className="shrink-0 p-4 border-t border-brand-soft bg-white space-y-2">
              <button
                type="button"
                onClick={goToCheckout}
                className="w-full bg-brand-primary text-white py-4 rounded-2xl font-display font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors active:scale-[0.98]"
              >
                Completar cotización <ArrowRight size={16} />
              </button>
              <button
                type="button"
                onClick={closeDrawer}
                className="w-full py-3 rounded-2xl font-bold text-xs uppercase tracking-widest text-brand-dark/50 hover:text-brand-primary hover:bg-brand-soft/30 transition-colors"
              >
                Seguir explorando
              </button>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuotationBasketDrawer;
